import { Disclosure, Transition } from "@headlessui/react";
import { useState, useEffect, useMemo, Fragment, useRef, useCallback, useLayoutEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFoldersContext } from "src/contexts/FolderContext";
import useForceUpdate from "src/hooks/useForceUpdate";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import {
  levelIcon,
  folderActiveIcon,
  folderActiveHoverIcon,
  folderIcon,
  folderHoverIcon,
} from "../ui_icons/Document_Icons";
import { FoldersType, Keys, Node } from "../type";
import _ from "lodash";
import {
  collectionsListName,
  dependenciesListName,
  favoriteFoldersListName,
  myDocumentsListName,
  typesListName,
} from "src/api/config";
import { getNavigation } from "src/components/Sidebar";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { NavItemType } from "src/layouts/DashboardLayout";
import { addItem, deleteItem, getItems, getItemsIdle } from "src/modules/spItems/actions";
import FavoriteButton from "./FavoriteButton";
import { CheckBoxListItem, FilterNode, LookUpRecord, UserListItem } from "src/types/main";
import { useFiltersContext } from "src/contexts/FilterContext";
import { selectCurrentUser } from "src/modules/users/selector";
import moment from "moment";
import useLatestClick from "src/hooks/useLatestClick";
import { useGetSelect } from "src/hooks/redux-selectors";
import { getItemsRequest } from "src/modules/spItems/api";

function FolderTree({ arrayNode, loading }: { arrayNode: Node[]; loading: boolean }) {
  const { forceUpdate } = useForceUpdate();
  const { openFolder, folders } = useFoldersContext();

  function updateNode(node: Node) {
    const newNode: FilterNode = { key: node.key, id: node.id, title: node.title };

    openFolder(node.key, newNode);
    forceUpdate();
  }

  if (arrayNode.length === 0) return null;

  return (
    <FlexCol className="w-full grow">
      {arrayNode.map((child) => (
        <FolderItem
          key={child.id}
          node={child}
          initOpen={child.id === folders[child.key as keyof typeof folders]?.id}
          toggleNode={updateNode}
        />
      ))}
    </FlexCol>
  );
}

function FolderItem({ node, toggleNode, initOpen }: { node: Node; toggleNode(node: Node): void; initOpen: boolean }) {
  const { closeFolder } = useFoldersContext();

  const isCollection = node.key === "collections";

  const contentRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(initOpen);

  function toggleOpen() {
    if (!isOpen) {
      toggleNode(node);
      setIsOpen(true);
    } else {
      closeFolder(node.key);
      setIsOpen(false);
    }
  }

  useEffect(() => {
    setIsOpen(initOpen);
  }, [initOpen]);

  return (
    <Disclosure as="div">
      <Disclosure.Button className="py-2.5" onClick={toggleOpen}>
        <FlexRow className="gap-x-2 items-top group">
          {!isCollection && <FlexCol>{levelIcon}</FlexCol>}
          <FlexCol>
            {isOpen ? (
              <>
                <span className="group-hover:hidden">{folderActiveIcon}</span>
                <span className="hidden group-hover:inline-block">{folderActiveHoverIcon}</span>
              </>
            ) : (
              <>
                <span className="group-hover:hidden">{folderIcon}</span>
                <span className="hidden group-hover:inline-block">{folderHoverIcon}</span>
              </>
            )}
          </FlexCol>
          <FlexCol className="ui-open:text-darkblue text-[#637381] text-left">{node.title}</FlexCol>
        </FlexRow>
      </Disclosure.Button>
      <Transition
        as={Fragment}
        enter="transition-all duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-all duration-300 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={isOpen}
      >
        <div className={twMerge(isCollection ? "ml-0" : "ml-3")} ref={contentRef}>
          <FolderTree arrayNode={node.children} loading={false} />
        </div>
      </Transition>
    </Disclosure>
  );
}

export function DocumentPageFolderBreadcrumbLayout() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectCurrentUser(state));

  const { data, status } = useGetSelect(favoriteFoldersListName);
  const { data: myDocumentsData } = useGetSelect(myDocumentsListName);

  const [isFavorite, setIsFavorite] = useState<{ state: boolean; id: number | null }>({
    state: false,
    id: null,
  });

  const { folders, clearFolders, isFoldersEmpty } = useFoldersContext();

  const { filters } = useFiltersContext();

  const buildBreadCrumbs = useCallback(
    (folders: FoldersType) => {
      const folderKeys = Object.keys(folders);

      const filtered = folderKeys.reduce((acc, currentKey) => {
        if (_.isNull(folders[currentKey as Keys])) {
          return acc;
        } else {
          acc.push(folders[currentKey as Keys]!.title);
        }

        return acc;
      }, [] as string[]);

      const values = _.values(_.omit(filters, [...folderKeys, "dateForRevision"])) as
        | CheckBoxListItem[][]
        | UserListItem[][];

      const rest = values.flatMap((v) => v.map((m) => m.title));

      const result = rest.length > 0 ? [...filtered, ...rest] : filtered;

      const { dateForRevision } = _.pick(filters, "dateForRevision");

      if (!_.isEmpty(_.values(dateForRevision.date))) {
        const { start, end } = dateForRevision.date;
        const startText = start ? "от " + moment(start).locale("ru").format("DD.MM.YYYY") : null;
        const endText = end ? "до " + moment(end).locale("ru").format("DD.MM.YYYY") : null;

        const dateText = (start === end ? [startText, null] : [startText, endText]).join(" ").trim();

        if (dateForRevision.required) {
          // const startRequiredText = "от " + moment(new Date()).locale("ru").format("DD.MM.YYYY");
          // const endRequiredText = "до " + moment(new Date()).add(30, "day").locale("ru").format("DD.MM.YYYY");
          // result.push([startRequiredText, endRequiredText].join(" "));

          result.push("Требуется пересмотр");
        }

        if (dateText !== "") {
          result.push(dateText);
        }
      }

      return result;
    },
    [JSON.stringify(filters)],
  ) as (folders: FoldersType) => string[];

  const breadcrumbs = useMemo(() => buildBreadCrumbs(folders).join(", "), [folders, buildBreadCrumbs]);

  const navItem: NavItemType | undefined = getNavigation(id).find((n) => n.url === pathname);

  const isEmpty = useMemo(() => _.every(folders, (value) => value === null), [folders]);

  const toggleFavorite = useLatestClick(() => {
    if (!isFavorite.state) {
      const valuesWithoutNull = _.filter(folders, (v) => v !== null) as FilterNode[];

      const lastFolder = valuesWithoutNull[valuesWithoutNull.length - 1];

      dispatch(
        addItem({
          listName: favoriteFoldersListName,
          body: {
            DocumentsFolder: JSON.stringify(folders),
            DocumentsCount: myDocumentsData.length,
            Title: lastFolder.title,
          },
          updateReducer: true,
        }),
      );
    } else {
      dispatch(
        deleteItem({
          listName: favoriteFoldersListName,
          ID: isFavorite.id,
          updateReducer: true,
        }),
      );
    }
  }, 300);

  useEffect(() => {
    if (status !== "LOADING" && folders) {
      const isHasFav = data.filter((folder) => _.isEqual(JSON.parse(folder.DocumentsFolder), folders));

      if (isHasFav.length > 0) {
        setIsFavorite({ state: true, id: isHasFav[0].ID });
      } else {
        setIsFavorite((prev) => ({ ...prev, state: false }));
      }
    }
  }, [data, folders, status]);

  useEffect(() => {
    if (user.email) {
      dispatch(
        getItems({
          listName: favoriteFoldersListName,
          filters: { Author: user.id },
        }),
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (status === "SUCCESS") {
      dispatch(getItemsIdle({ listName: favoriteFoldersListName }));
    }
  }, [status, dispatch]);

  if (!navItem) {
    navigate("/");
    return null;
  }

  return (
    <div className="flex justify-between w-full" aria-label="Breadcrumb">
      <FlexCol>
        <ol className="inline-flex items-center">
          <li className="inline-flex items-center" aria-current="page">
            <div
              className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer"
              onClick={() => clearFolders()}
            >
              {navItem.icon ?? null}
              <span>{navItem.name}</span>
            </div>
            <span className="px-3 text-body-color">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                  fill={"#637381"}
                />
              </svg>
            </span>
          </li>
          <li className="inline-flex items-center" aria-current="page">
            <div
              className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer"
              onClick={() => clearFolders()}
            >
              <span className={isEmpty ? "text-darkblue" : ""}>Все</span>
            </div>
            {(!isEmpty || breadcrumbs.length > 0) && (
              <span className="px-3 text-body-color">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                    fill={"#637381"}
                  />
                </svg>
              </span>
            )}
          </li>
          <li aria-current="page" className="space-x-1">
            <div className="flex items-center w-full text-base font-medium">{breadcrumbs}</div>
          </li>
        </ol>
      </FlexCol>
      <FlexCol>{isFoldersEmpty() && <FavoriteButton isFavorite={isFavorite} onToggle={toggleFavorite} />}</FlexCol>
    </div>
  );
}

interface Item {
  ID: number;
  Collection: { Title: string; ID: number };
  Department: { Title: string; ID: number };
  Process: { Title: string; ID: number };
  Sector: { Title: string; ID: number };
}

function Folders() {
  const { statusFilters } = useFiltersContext();

  return (
    <FlexCol className="py-2.5 px-[18px] w-full">
      <FiltersAppliedNotification />
      <div className={statusFilters ? twMerge("opacity-50 filter grayscale pointer-events-none") : ""}>
        <MainTree />
      </div>
    </FlexCol>
  );
}

export default Folders;

function MainTree() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  const { folders } = useFoldersContext();

  // Raw dependency items fetched from SP (only re-fetched when selected collection changes)
  const [rawDeps, setRawDeps] = useState<Item[]>([]);
  const [subData, setSubData] = useState<Node[]>([]);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [depsLoading, setDepsLoading] = useState(false);

  const { data: dataTypes, status: typesStatus } = useGetSelect(typesListName);
  const { data: dataCollections, status: collectionsStatus } = useGetSelect(collectionsListName);

  // Ensure collections are loaded if initDataSaga hasn't run yet (e.g. direct URL open / page refresh)
  useEffect(() => {
    if (collectionsStatus === "IDLE") {
      dispatch(getItems({ listName: collectionsListName, sortOrder: { name: "SortColumn", asc: true } }));
    }
  }, [collectionsStatus, dispatch]);

  // Ensure types are loaded (needed for process → type leaf nodes)
  useEffect(() => {
    if (typesStatus === "IDLE") {
      dispatch(getItems({ listName: typesListName, sortOrder: { name: "SortColumn", asc: true } }));
    }
  }, [typesStatus, dispatch]);

  function addOrUpdateNode(parentChildren: Node[], id: number, key: string, title: string): Node {
    let node = parentChildren.find((child) => child.id === id);
    if (!node) {
      node = { id: id, key, title, children: [] };
      parentChildren.push(node);
    }
    return node;
  }

  // Step 1: Fetch raw SP dependency data only when selected collection changes
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!folders.collections) {
        setRawDeps([]);
        return;
      }

      try {
        setDepsLoading(true);

        const dataDependencies = await getItemsRequest({
          listName: dependenciesListName,
          filters: { Collection: folders.collections.id },
          sortOrder: { name: "SortDepend", asc: true },
        });

        if (cancelled) return;

        const processed = dataDependencies.items.reduce((acc, cur) => {
          acc.push({
            ID: cur.ID,
            Collection: {
              Title: cur.Collection.get_lookupValue(),
              ID: +cur.Collection.get_lookupId(),
            },
            Department: {
              Title: cur.Department.get_lookupValue(),
              ID: +cur.Department.get_lookupId(),
            },
            Process: {
              Title: cur.Process.get_lookupValue(),
              ID: +cur.Process.get_lookupId(),
            },
            Sector: {
              Title: cur.Sector.get_lookupValue(),
              ID: +cur.Sector.get_lookupId(),
            },
          });
          return acc;
        }, [] as Item[]);

        setRawDeps(processed);
      } catch (error) {
        console.log("fetchData => folders.collections", error);
      } finally {
        if (!cancelled) setDepsLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [folders.collections]);

  // Step 2: Build tree from raw deps + current types.
  // Runs whenever raw deps OR types change — fixes the stale-closure bug where
  // types loaded after the initial fetch were never reflected in the tree.
  useEffect(() => {
    if (rawDeps.length === 0) {
      setSubData([]);
      return;
    }

    const newTypesData = dataTypes.map((v) => ({ id: v.ID, title: v.Title, key: "types", children: [] as Node[] }));

    function buildFolderTree(data: Item[]): Node[] {
      const root: Node[] = [];
      data.forEach((item) => {
        const departmentNode = addOrUpdateNode(root, item.Department.ID, "departments", item.Department.Title);
        const sectorNode = addOrUpdateNode(departmentNode.children, item.Sector.ID, "sectors", item.Sector.Title);
        const processNode = addOrUpdateNode(sectorNode.children, item.Process.ID, "processes", item.Process.Title);
        processNode.children = newTypesData;
      });
      return root;
    }

    setSubData(buildFolderTree(rawDeps));
  }, [rawDeps, dataTypes]);

  useLayoutEffect(() => {
    const filteredCollections = _.filter(dataCollections, (collection) => {
      // Если Hide: false, включаем коллекцию
      if (!collection.Hide) return true;

      // Если Hide: true, проверяем наличие пересечения групп
      return _.some(collection.Groups, (group) => _.some(currentUser.groups, { Id: group.ID }));
    });

    const arrayNode = filteredCollections.map((collection) => ({
      id: collection.ID,
      title: collection.Title,
      children: collection.ID === folders.collections?.id ? subData : [],
      key: "collections",
    }));

    setNodes(arrayNode);
  }, [currentUser.groups, folders.collections, subData, dataCollections]);

  const collectionsLoading = collectionsStatus === "IDLE" || collectionsStatus === "LOADING";

  if (collectionsLoading) {
    return (
      <FlexCol className="gap-y-2 py-2.5 w-full">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-8 bg-gray-100 animate-pulse rounded-md" />
        ))}
      </FlexCol>
    );
  }

  return <FolderTree arrayNode={nodes} loading={depsLoading} />;
}

function FiltersAppliedNotification() {
  const { statusFilters, resetFilters } = useFiltersContext();

  return (
    <>
      {statusFilters && (
        <div>
          Применены фильтр. Чтобы перейти к папкам,{" "}
          <span className="underline cursor-pointer hover:no-underline" onClick={resetFilters}>
            {" "}
            сбросьте фильтры
          </span>
        </div>
      )}
    </>
  );
}
